import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChatCircleDots, X, PaperPlaneRight } from "@phosphor-icons/react";

export default function HelpChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your GrantFlow help assistant. Ask anything — search filters, generation, submitting, billing, etc." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const userMsg = { role: "user", content: input.trim() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/help/chat", {
        message: userMsg.content,
        history: next.slice(-6),
      });
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: "Sorry — I'm having trouble. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="help-widget-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 bg-blue-700 hover:bg-blue-800 text-white px-4 py-3 gf-sharp shadow-lg flex items-center gap-2 font-bold uppercase tracking-wide text-xs z-50"
        >
          <ChatCircleDots size={18} weight="fill" /> Help
        </button>
      )}
      {open && (
        <div
          data-testid="help-widget-panel"
          className="fixed bottom-6 right-6 w-[360px] max-w-[calc(100vw-3rem)] h-[480px] max-h-[calc(100vh-4rem)] bg-white border border-zinc-300 flex flex-col z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50">
            <div>
              <div className="gf-overline">Help Assistant</div>
              <div className="font-display font-bold tracking-tight">Ask GrantFlow</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="help-widget-close" className="text-zinc-500 hover:text-zinc-900">
              <X size={20} />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
                <div
                  className={`inline-block px-3 py-2 max-w-[85%] gf-sharp ${
                    m.role === "user"
                      ? "bg-blue-700 text-white"
                      : "bg-zinc-100 text-zinc-900 border border-zinc-200"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="text-xs text-zinc-500 gf-overline">Thinking…</div>
            )}
          </div>
          <form onSubmit={send} className="p-3 border-t border-zinc-200 flex gap-2">
            <Input
              data-testid="help-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              className="gf-sharp border-zinc-300 bg-white"
            />
            <Button
              type="submit"
              data-testid="help-widget-send"
              disabled={sending}
              className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white"
            >
              <PaperPlaneRight size={16} />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
